import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import type { User } from '../../../shared/types';
import { AuthService } from '../services/auth.service';
import { AuthMockService } from '../services/auth-mock.service';
import { LoggerService } from '../../../shared/services/logger.service';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Detectar si Firebase está configurado
  const isFirebaseConfigured = useMemo(() => {
    const hasApiKey = !!import.meta.env.VITE_FIREBASE_API_KEY;
    const hasProjectId = !!import.meta.env.VITE_FIREBASE_PROJECT_ID;
    return hasApiKey && hasProjectId;
  }, []);

  const authService = useMemo(() => {
    if (isFirebaseConfigured) {
      LoggerService.info('Using Firebase Auth', 'AuthProvider');
      return new AuthService();
    } else {
      LoggerService.info('Using Mock Auth (Firebase not configured)', 'AuthProvider');
      return new AuthMockService();
    }
  }, [isFirebaseConfigured]);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange((user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [authService]);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const user = await authService.login(email, password);
      setUser(user);
    } catch (error) {
      setLoading(false);
      throw error;
    }
    setLoading(false);
  };

  const register = async (email: string, password: string, displayName?: string) => {
    setLoading(true);
    try {
      const user = await authService.register(email, password, displayName);
      setUser(user);
    } catch (error) {
      setLoading(false);
      throw error;
    }
    setLoading(false);
  };

  const logout = async () => {
    setLoading(true);
    try {
      await authService.logout();
      setUser(null);
      // Opcional: limpiar cache al hacer logout explícito
      // UserCacheService.clearUser();
    } catch (error) {
      setLoading(false);
      throw error;
    }
    setLoading(false);
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
